import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/context/AuthContext';
import { LogIn, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/app';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      setApiError(null);
      const { error } = await signIn(data.email, data.password);
      if (error) {
        setApiError(error.message);
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      setApiError(err instanceof Error ? err.message : 'An unexpected login error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md bg-white rounded-xl border border-brand-border shadow-lg p-6 sm:p-8">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-2 bg-white rounded-lg border border-brand-border shadow-xs mb-4">
            <img
              src="/reference/al-kuwait-tech-logo.png"
              alt="Al Kuwait Tech Logo"
              className="h-16 w-auto object-contain mx-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-brand-black tracking-tight">
            AL KUWAIT TECH
          </h1>
          <p className="text-xs text-brand-muted mt-1">
            Staff Authentication & Management Portal
          </p>
        </div>

        {/* Error Alert */}
        {apiError && (
          <div className="mb-6 p-4 bg-brand-red-light border border-brand-red/30 rounded-md flex items-start space-x-3 text-brand-red text-xs">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Authentication Failed</p>
              <p className="mt-0.5 text-brand-red/90">{apiError}</p>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
              Email Address
            </label>
            <input
              type="email"
              placeholder="name@alkuwaittech.com"
              {...register('email')}
              className={`w-full px-3 py-2.5 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
                errors.email ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-brand-red">{errors.email.message}</p>
            )}
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-brand-green hover:underline font-medium"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                className={`w-full px-3 py-2.5 bg-brand-bg border rounded-md text-sm text-brand-black placeholder-brand-muted pr-10 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all ${
                  errors.password ? 'border-brand-red focus:ring-brand-red' : 'border-brand-border'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-black"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-brand-red">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full inline-flex items-center justify-center space-x-2 py-2.5 px-4 bg-brand-green text-white font-semibold text-sm rounded-md hover:bg-brand-green-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-green transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>Sign In to Staff Portal</span>
              </>
            )}
          </button>
        </form>

        {/* Security Footer Notice */}
        <div className="mt-8 pt-6 border-t border-brand-border text-center">
          <div className="flex items-center justify-center space-x-1.5 text-[11px] text-brand-muted">
            <ShieldCheck className="w-4 h-4 text-brand-green" />
            <span>Protected by Supabase Auth & Role-Based Access Control</span>
          </div>
        </div>
      </div>
    </div>
  );
};
